import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { getPricingTreatments, hasCustomPricingData, getTranslatedName } from '@/lib/treatmentStore';

const surgerySlugs: Record<string, string> = {
  'Bauchdeckenstraffung': 'abdominoplasty',
  'Nasenkorrektur': 'rhinoplasty',
  'Nasenkorrektur + Septumkorrektur': 'rhinoplasty-septoplasty',
  'Nasenkorrektur Revision': 'rhinoplasty-revision',
  'Ohrkorrektur': 'otoplasty',
  'Unterlidstraffung': 'lower-blepharoplasty',
  'Oberlidstraffung': 'upper-blepharoplasty',
  'Komplette Lidstraffung': 'total-blepharoplasty',
  'Brustvergrößerung (Mentor)': 'breast-augmentation',
  'Brustverkleinerung': 'breast-reduction',
  'Facelifting': 'facelift',
  'Facelifting (Mentor)': 'facelift-mentor',
  'Gynäkomastie': 'gynecomastia',
  'Bauchdeckenstraffung + Liposuktion': 'abdominoplasty-liposuction',
  'Liposuktion + BBL': 'liposuction-bbl',
  'Liposuktion (1 Zone)': 'liposuction-one-zone',
  'Liposuktion (Total Body)': 'liposuction-total-body',
  'Gesichtslifting': 'face-lift',
  'Oberarmstraffung': 'arm-lift',
  'Oberschenkelstraffung': 'thigh-lift',
  'Magenballon': 'gastric-balloon',
};

type Category = {
  key: string;
  icon: string;
  names: string[];
};

const CATEGORIES: Category[] = [
  {
    key: 'face',
    icon: 'ri-user-smile-line',
    names: ['Nasenkorrektur', 'Nasenkorrektur + Septumkorrektur', 'Nasenkorrektur Revision', 'Ohrkorrektur', 'Unterlidstraffung', 'Oberlidstraffung', 'Komplette Lidstraffung', 'Facelifting', 'Facelifting (Mentor)', 'Gesichtslifting'],
  },
  {
    key: 'breast',
    icon: 'ri-heart-pulse-line',
    names: ['Brustvergrößerung (Mentor)', 'Brustverkleinerung', 'Gynäkomastie'],
  },
  {
    key: 'body',
    icon: 'ri-body-scan-line',
    names: ['Bauchdeckenstraffung', 'Bauchdeckenstraffung + Liposuktion', 'Liposuktion + BBL', 'Liposuktion (1 Zone)', 'Liposuktion (Total Body)', 'Oberarmstraffung', 'Oberschenkelstraffung'],
  },
  {
    key: 'special',
    icon: 'ri-empathize-line',
    names: ['Magenballon'],
  },
];

const CATEGORY_LABEL_KEYS: Record<string, string> = {
  face: 'svc.surgery_cat_face_label',
  breast: 'svc.surgery_cat_breast_label',
  body: 'svc.surgery_cat_body_label',
  special: 'svc.surgery_cat_special_label',
};

const CATEGORY_DESC_KEYS: Record<string, string> = {
  face: 'svc.surgery_cat_face_desc',
  breast: 'svc.surgery_cat_breast_desc',
  body: 'svc.surgery_cat_body_desc',
  special: 'svc.surgery_cat_special_desc',
};

interface TreatmentItem {
  name: string;
  price: string;
  stay?: string;
}

function getTreatments(lang: string): TreatmentItem[] {
  return getPricingTreatments('surgery', lang);
}

function getSlugByName(name: string): string | undefined {
  return surgerySlugs[name];
}

export default function SurgeryPriceTable() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const items = getTreatments(lang);
  const isCustom = hasCustomPricingData();

  const getItemByGermanName = (deName: string): TreatmentItem | null => {
    const translated = getTranslatedName(deName, lang);
    return items.find((item) => item.name === translated) || null;
  };

  const renderTreatmentRow = (item: TreatmentItem, idx: number) => {
    const slug = getSlugByName(item.name);

    return (
      <div
        key={idx}
        className="group flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 md:px-5 md:py-4 rounded-xl bg-background-50 border border-background-200/70 hover:border-secondary-200 hover:shadow-sm transition-all duration-300"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 rounded-lg bg-background-100 mt-0.5">
              <i className="ri-arrow-right-up-line text-sm text-foreground-400"></i>
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-label text-sm font-semibold text-foreground-900 group-hover:text-secondary-700 transition-colors">
                {item.name}
              </h4>
              {item.stay && (
                <p className="text-[11px] text-foreground-400 font-body mt-0.5 flex items-center gap-1">
                  <i className="ri-hotel-bed-line text-[10px]"></i>
                  {item.stay}
                </p>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4 sm:gap-6 sm:flex-shrink-0">
          <div className="text-right">
            <p className="font-heading text-lg font-bold text-secondary-600 whitespace-nowrap">
              {item.price} €
            </p>
            <p className="text-[10px] text-foreground-400 font-body">
              {String(item.price).includes('–') ? t('svc.price_from') : t('svc.price_fixed')}
            </p>
          </div>
          {slug && (
            <Link
              to={`/services/${slug}`}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs text-secondary-600 border border-secondary-200 hover:bg-secondary-50 font-label transition-colors cursor-pointer no-underline whitespace-nowrap"
            >
              {t('pricing.view_details')}
              <i className="ri-arrow-right-line text-[10px]"></i>
            </Link>
          )}
        </div>
      </div>
    );
  };

  return (
    <section id="surgery" className="py-16 md:py-24 px-4 md:px-10 bg-background-100">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-12 md:mb-16">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-[10px] md:text-xs tracking-[0.15em] uppercase font-label">
            <i className="ri-surgical-mask-line text-xs"></i>
            {t('pricing.surgery_title')}
          </span>
          <h2 className="mt-4 font-heading text-2xl md:text-4xl font-bold text-foreground-900">
            {t('pricing.surgery_title')}
          </h2>
          <p className="mt-3 text-sm text-foreground-500 font-body max-w-lg mx-auto leading-relaxed break-words">
            {t('svc.surgery_subtitle')}
          </p>
        </div>

        {isCustom ? (
          /* Flat list for custom data */
          <div className="grid grid-cols-1 gap-2.5">
            {items.map((item, idx) => renderTreatmentRow(item, idx))}
          </div>
        ) : (
          /* Categorized view for default data */
          <div className="space-y-10">
            {CATEGORIES.map((cat) => {
              const catItems = cat.names
                .map(getItemByGermanName)
                .filter((item): item is NonNullable<typeof item> => item !== null);

              if (catItems.length === 0) return null;

              return (
                <div key={cat.key}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-secondary-100">
                      <i className={`${cat.icon} text-lg text-secondary-600`}></i>
                    </div>
                    <div>
                      <h3 className="font-label text-base font-semibold text-foreground-900">
                        {CATEGORY_LABEL_KEYS[cat.key] ? t(CATEGORY_LABEL_KEYS[cat.key]) : cat.key}
                      </h3>
                      <p className="text-xs text-foreground-500 font-body mt-0.5">
                        {CATEGORY_DESC_KEYS[cat.key] ? t(CATEGORY_DESC_KEYS[cat.key]) : ''}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-2.5">
                    {catItems.map((item, idx) => renderTreatmentRow(item, idx))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <p className="mt-8 text-center text-xs text-foreground-400 font-body">
          {t('pricing.note_individual')}
        </p>
      </div>
    </section>
  );
}